import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import PermissionSettingsMatrix from './permission-settings-matrix.component';
import { buildInitialOverrideFormForDialog } from './job-settings-personal-permission.utils';
import type { EmployeePermissionClassRow } from './permission-settings.types';
import i18n from '../../i18n';
import './scss/permission-settings.page.scss';

export type JobSettingsExceptionPermissionDialogProps = {
    open: boolean;
    /** 关闭弹窗（取消或点遮罩） */
    onClose: () => void;
    /** 确定：提交矩阵表单态 */
    onConfirm: (form: EmployeePermissionClassRow) => void;
    personalSupplement: boolean;
    overrideForm: EmployeePermissionClassRow | null;
    templateRow: EmployeePermissionClassRow | null;
};

/**
 * 岗位设置 — 新增/编辑个人权限例外（Figma 7322:108291）：复用权限设置矩阵。
 */
export default function JobSettingsExceptionPermissionDialog(props: JobSettingsExceptionPermissionDialogProps) {
    const { open, onClose, onConfirm, personalSupplement, overrideForm, templateRow } = props;
    const [formState, setFormState] = useState<EmployeePermissionClassRow>({});

    useEffect(() => {
        if (!open) {
            return;
        }
        setFormState(
            _.cloneDeep(buildInitialOverrideFormForDialog(personalSupplement, overrideForm, templateRow)),
        );
    }, [open, personalSupplement, overrideForm, templateRow]);

    const onToggleCheckbox = useCallback((key: string, checked: boolean) => {
        setFormState((prev) => ({
            ...prev,
            [key]: checked ? 1 : 0,
        }));
    }, []);

    const handleConfirm = useCallback(() => {
        onConfirm(_.cloneDeep(formState));
        onClose();
    }, [formState, onConfirm, onClose]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    bgcolor: 'var(--fill-neutral-100)',
                    maxHeight: 'min(90vh, 900px)',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'var(--text-dark)' }}>
                    {i18n.t('job_settings_exception_permission_dialog_title')}
                </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ px: 3, py: 2 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 2, color: 'var(--text-dark)' }}>
                    {i18n.t('permission_settings_section_role_permissions')}
                </Typography>
                <Box className="job-settings-exception-permission-dialog__matrix">
                    <PermissionSettingsMatrix
                        formState={formState}
                        onToggle={onToggleCheckbox}
                        checkboxIdPrefix="job-settings-exception-cb"
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button
                    type="button"
                    variant="text"
                    onClick={onClose}
                    sx={{
                        flex: 1,
                        minHeight: 56,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: 20,
                        fontWeight: 700,
                        color: 'var(--text-secondary)',
                        bgcolor: 'var(--icon-neutral-200)',
                        border: '1px solid var(--stroke-neutral-800-a25)',
                    }}
                >
                    {i18n.t('google_text_public_button_cancel')}
                </Button>
                <Button
                    type="button"
                    variant="contained"
                    onClick={handleConfirm}
                    sx={{
                        flex: 1,
                        minHeight: 56,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: 20,
                        fontWeight: 700,
                        bgcolor: 'var(--fill-primary-300)',
                        color: 'var(--text-light)',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: 'var(--fill-primary-300)', opacity: 0.92, boxShadow: 'none' },
                    }}
                >
                    {i18n.t('google_text_confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
